import type { TechnicalConfigFieldKind } from "./fieldKindStrategies";

import { getFieldKindStrategy } from "./fieldKindStrategies";

type FormStateFieldDefinition<TKey extends string = string> = {
  default: boolean | null | string;
  key: TKey;
  kind: TechnicalConfigFieldKind;
};

const isCompleteFormState = <TState extends Record<string, boolean | string>>(
  state: Partial<TState>,
  fields: readonly { key: keyof TState & string }[]
): state is TState => fields.every((field) => field.key in state);

const assignFormFieldValue = <TState extends Record<string, boolean | string>>(
  state: Partial<TState>,
  key: keyof TState & string,
  value: boolean | string
): void => {
  Object.assign(state, { [key]: value });
};

export const buildDefaultFormState = <TState extends Record<string, boolean | string>>(
  fields: readonly FormStateFieldDefinition[]
): TState => {
  const state: Partial<TState> = {};

  fields.forEach((field) => {
    assignFormFieldValue(state, field.key, getFieldKindStrategy(field.kind).getEmptyFormValue());
  });

  if (!isCompleteFormState(state, fields)) {
    throw new Error("Failed to build form state");
  }

  return state;
};

export const mapConfigToFormState = <
  TState extends Record<string, boolean | string>,
  TConfig extends Record<string, unknown>,
  TFieldKey extends keyof TState & string = keyof TState & string
>({
  config,
  fields,
  getRawValue,
}: {
  config?: null | TConfig;
  fields: readonly FormStateFieldDefinition<TFieldKey>[];
  getRawValue: (config: TConfig | undefined, field: FormStateFieldDefinition<TFieldKey>) => unknown;
}): TState => {
  const state: Partial<TState> = {};

  fields.forEach((field) => {
    const rawValue = getRawValue(config ?? undefined, field);
    assignFormFieldValue(state, field.key, getFieldKindStrategy(field.kind).apiToFormValue(rawValue));
  });

  if (!isCompleteFormState(state, fields)) {
    throw new Error("Failed to map config to form state");
  }

  return state;
};

export const mapFormStateToPayload = <TState extends Record<string, boolean | string>>(
  formState: TState,
  fields: readonly FormStateFieldDefinition[]
): Record<string, boolean | null | number | string> =>
  Object.fromEntries(
    fields.map((field) => [field.key, getFieldKindStrategy(field.kind).formToApiValue(formState[field.key])])
  );
